import { Box, Button, Heading, Input, Stack, Text, ThemeContext } from '@vouched.id/vault';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VouchedLogo } from '../reusable/vouched-logo';
import { setAuthData, setMessages, setPasswordLoginData } from 'store/actions/actions';
import { useGlobalState } from 'store/reducers/reducer';
import { finalizeLogin, initiateLogin } from 'store/sagas/sagas';
import { humanDate } from 'utils/ui.utils';
import { FieldState, formatInput } from 'utils/validation';
export const Signin = () => {
    const { state, dispatch, asyncDispatch } = useGlobalState();
    const navigate = useNavigate();
    const { tokens } = useContext(ThemeContext).theme;
    const desktopLogoColor = tokens.colors.backgroundPrimary;
    const [searchParams] = useSearchParams();
    const [signinProfile, setSigninProfile] = useState({
        email: ''
    });
    const [isInvalid, setIsInvalid] = useState({
        email: FieldState.NOT_SET
    });
    useEffect(() => {
        const currentEmail = state.signinEmail;
        if (currentEmail) {
            setSigninProfile((prevState) => ({
                ...prevState,
                email: currentEmail
            }));
        }
        const accessToken = searchParams.get('auth-token');
        if (accessToken) {
            finalizeSso(accessToken);
        }
    }, [state.signinEmail]);
    const handleChange = useCallback((e) => {
        const { name } = e.target;
        let { value } = e.target;
        if (name === 'email') {
            const { formattedValue } = formatInput(e);
            value = formattedValue;
        }
        setSigninProfile((prevState) => ({
            ...prevState,
            [name]: value
        }));
    }, []);
    const handleBlur = (e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            if (value === '') {
                setIsInvalid({ ...isInvalid, [name]: FieldState.NOT_SET });
            }
            else {
                const { isValid } = formatInput(e);
                setIsInvalid({
                    ...isInvalid,
                    [name]: isValid ? FieldState.VALID : FieldState.INVALID
                });
            }
        }
    };
    const [isLoading, setIsLoading] = useState(false);
    const submit = (event) => {
        event.preventDefault();
        if (isLoading) {
            return;
        }
        const { email } = signinProfile;
        if (email) {
            setIsLoading(true);
            asyncDispatch(initiateLogin(email))
                .then((data) => {
                if (data.type === 'sso_error') {
                    navigate('/signin-error');
                }
                else if (data.type === 'password_login') {
                    dispatch(setPasswordLoginData({
                        email,
                        ssoBackup: data.ssoBackup
                    }));
                    navigate('/signin-password');
                }
                else {
                    // SSO redirect (data.type === 'redirect')
                    window.location.href = data.url;
                }
            })
                .catch((error) => {
                setIsLoading(false);
                setIsInvalid({
                    email: FieldState.INVALID
                });
                console.error('Signin Error', error);
            });
        }
    };
    const finalizeSso = (accessToken) => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        asyncDispatch(finalizeLogin(accessToken))
            .then((data) => {
            dispatch(setMessages({
                severity: 'info',
                value: `Last login at ${humanDate(data.user.loginAt)}`
            }));
            dispatch(setAuthData(data));
            navigate('/account/jobs');
        })
            .catch((error) => {
            setIsLoading(false);
            setIsInvalid({
                email: FieldState.INVALID
            });
            console.error('Signin Error', error);
        });
    };
    const { brightGreen, midPurple } = tokens.colors;
    return (React.createElement(Box, { display: ['block', 'flex'], height: "100vh", padding: ['spacing.large', 'spacing.none'], style: { boxSizing: 'border-box', backgroundColor: midPurple }, tag: "main" },
        React.createElement(Box, { alignItems: "center", backgroundColor: "colors.backgroundPrimary", display: "flex", flex: "1", flexDirection: "column", justifyContent: "center", paddingX: "spacing.large", paddingY: "spacing.xlarge", position: "relative", style: {
                borderRadius: ['spacing.default', '0'],
                boxSizing: 'border-box',
                top: '50%',
                transform: 'translateY(-50%)'
            } },
            React.createElement(Box, { display: ['block', 'none'], marginBottom: "spacing.large", textAlign: "center" },
                React.createElement(VouchedLogo, { fill: midPurple, maxWidth: "none", width: "100%" }),
                React.createElement(Box, { marginBottom: "spacing.xlarge", marginTop: "spacing.default" },
                    React.createElement(Text, { style: {
                            color: 'colors.textMuted',
                            fontWeight: '600',
                            fontSize: ['text.size.default', null, 'text.size.large']
                        }, tag: "span" }, "It's Who You Know")),
                React.createElement(Heading, { style: {
                        fontWeight: 'bold'
                    }, tag: "h1", variation: "h3" }, "Sign In")),
            React.createElement(Box, { display: ['none', 'block'], marginBottom: "spacing.xlarge2" },
                React.createElement(Heading, { style: { fontWeight: 'bold' }, tag: "h1", variation: "h3" }, "Sign In to Your Account")),
            React.createElement(Box, { maxWidth: "52rem", noValidate: true, onSubmit: submit, tag: "form", width: "100%" },
                React.createElement(Stack, { spacing: "spacing.large" },
                    React.createElement(Box, null,
                        React.createElement(Input, { autoComplete: "email", inputMode: "email", isInvalid: isInvalid['email'] === FieldState.INVALID, label: "Email", name: "email", onBlur: handleBlur, onChange: handleChange, validationMessage: "Invalid email address.", value: signinProfile.email })),
                    React.createElement(Box, { alignItems: "center", display: "flex", justifyContent: "center", marginTop: "spacing.default", paddingTop: "spacing.default", width: "100%" },
                        React.createElement(Button, { "data-cy": "submit", style: { backgroundColor: midPurple }, title: isLoading ? 'loading' : '' }, "Sign In"))))),
        React.createElement(Box, { alignItems: "center", display: ['none', 'flex'], flex: "1", flexDirection: "column", justifyContent: "center", paddingX: "spacing.default" },
            React.createElement(VouchedLogo, { fill: desktopLogoColor, maxWidth: "50rem", width: "80%" }),
            React.createElement(Box, { marginTop: "spacing.large" },
                React.createElement(Text, { style: {
                        color: brightGreen,
                        fontWeight: '600',
                        fontSize: ['text.size.default', null, 'text.size.large']
                    }, tag: "span" }, "It's Who You Know")))));
};
